import * as XLSX from 'xlsx'

/**
 * データを Excel 形式に変換してダウンロード
 * @param data - データ配列
 * @param columns - カラム定義（label と accessor）
 * @param filename - ファイル名（.xlsx 拡張子なし）
 */
export function downloadExcel<T>(
  data: T[],
  columns: { label: string; accessor: (row: T) => unknown }[],
  filename: string,
): void {
  // ヘッダー行を作成
  const headers = columns.map((col) => col.label)

  // データ行を作成
  const rows = data.map((row) => {
    return columns.map((col) => {
      const value = col.accessor(row)
      return formatValue(value)
    })
  })

  // ヘッダーとデータを結合
  const worksheetData = [headers, ...rows]

  // ワークシートを作成
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

  // カラム幅を自動調整（全角文字は2倍の幅として計算）
  const getCharWidth = (str: string): number => {
    return Array.from(str).reduce((width, char) => {
      const code = char.charCodeAt(0)
      // ASCII印字可能文字（0x20-0x7E）は半角、それ以外は全角として扱う
      const isHalfWidth = code >= 0x20 && code <= 0x7e
      return width + (isHalfWidth ? 1 : 2)
    }, 0)
  }
  const columnWidths = columns.map((col, colIndex) => {
    const headerWidth = getCharWidth(col.label)
    const maxDataWidth = Math.max(
      ...rows.map((row) => {
        const cellValue = String(row[colIndex] ?? '')
        return getCharWidth(cellValue)
      }),
      0,
    )
    return { wch: Math.max(headerWidth, maxDataWidth, 10) }
  })
  worksheet['!cols'] = columnWidths

  // ワークブックを作成
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'データ')

  // Excel ファイルとしてダウンロード
  XLSX.writeFile(workbook, `${filename}.xlsx`)
}

/**
 * xlsx ライブラリが適切に処理できる型に変換するため、
 * 文字列・数値・真偽値・Dateはそのまま、それ以外は文字列化する
 */
function formatValue(value: unknown): string | number | boolean | Date | null {
  if (value === null || value === undefined) {
    return null
  }
  if (value instanceof Date) {
    return value
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value
  }
  return String(value)
}
